import React, { useState, useMemo } from 'react';
import { faCheck, faCheckCircle, faExclamationCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  StatusBadge,
  ActionButton,
  createTextColumn,
  createEnumColumn,
  createCustomColumn,
  createDateColumn,
  useTableState,
  useColumnSettings,
  OutOfOfficeMark,
  outOfOfficeForAssignee,
} from '@medprompt/shared';
import { TaskTypeEnum } from '../enums/taskTypeEnum';
import { UrgencyEnum } from '../enums/urgencyEnum';

/** Hidden column keys until the user saves column settings — must match modal `defaultHidden`. */
const OUTCOME_TASKS_TABLE_DEFAULT_HIDDEN_COLUMNS = ['taskType'];

const useOutcomeTaskTable = (list, taskViewerContext) => {
  const { hasViewers, itemViewers: taskViewers } = taskViewerContext || {};
  const {
    outcomeTaskManager,
    addNotification,
    applyTaskPatch,
    removeTaskLocally,
    setTasks,
    handleMarkComplete,
    handleDeleteTask,
    getAssigneeName,
    activeUsers,
  } = list;

  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [bulkMarkCompleteModalOpen, setBulkMarkCompleteModalOpen] = useState(false);
  const [tasksForBulkAction, setTasksForBulkAction] = useState([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkMarkCompleteLoading, setBulkMarkCompleteLoading] = useState(false);
  const [columnCustomizationOpen, setColumnCustomizationOpen] = useState(false);

  const {
    sortState: tableSortState,
    setSortState: setTableSortState,
    filterValues: tableFilterValues,
    setFilterValues: setTableFilterValues,
    disabledFilters: tableDisabledFilters,
    setDisabledFilters: setTableDisabledFilters,
    paginationState: tablePaginationState,
    setPaginationState: setTablePaginationState,
    highlightedRows,
    setHighlightedRows
  } = useTableState('OutcomeTasksTable', {
    defaultSortState: { columnKey: 'timestamp', direction: 'asc' }
  });

  const clearAssigneeFilter = () => {
    setTableFilterValues(prev => {
      const newFilters = { ...prev };
      delete newFilters.assigneeId;
      return newFilters;
    });
    setTableDisabledFilters(prev => {
      const newSet = new Set(prev);
      newSet.delete('assigneeId');
      return newSet;
    });
  };

  const handleBulkDeleteTasks = (selectedTasksData) => {
    if (selectedTasksData.length === 0) return;
    setTasksForBulkAction(selectedTasksData);
    setBulkDeleteModalOpen(true);
  };

  const handleBulkMarkComplete = (selectedTasksData) => {
    if (selectedTasksData.length === 0) return;
    const incompleteTasks = selectedTasksData.filter(task => !task.isComplete);
    if (incompleteTasks.length === 0) {
      addNotification('bulk-mark-complete-none', 'No incomplete tasks selected', { isError: true });
      return;
    }
    setTasksForBulkAction(incompleteTasks);
    setBulkMarkCompleteModalOpen(true);
  };

  const handleBulkMarkUrgent = (selectedTasksData) => {
    if (selectedTasksData.length === 0) return;

    const updatePromises = selectedTasksData.map(task =>
      new Promise((resolve, reject) => {
        outcomeTaskManager.updateOutcomeTask({
          taskId: task.taskId,
          isUrgent: true
        })
          .then(() => {
            addNotification(`bulk-urgent-${task.taskId}`, `Task for ${task.patient?.name} marked as urgent`);
            resolve(task);
          })
          .catch((error) => {
            console.error(`Failed to mark task ${task.taskId} as urgent:`, error);
            addNotification(`bulk-urgent-error-${task.taskId}`, `Failed to mark task for ${task.patient?.name} as urgent`, { isError: true });
            reject(error);
          });
      })
    );

    Promise.allSettled(updatePromises).then(() => {
      setTasks(prevTasks =>
        prevTasks.map(task =>
          selectedTasksData.find(bulkTask => bulkTask.taskId === task.taskId)
            ? { ...task, isUrgent: true }
            : task
        )
      );
      setSelectedRows(new Set());
    });
  };

  const handleBulkMarkNormal = (selectedTasksData) => {
    if (selectedTasksData.length === 0) return;

    const updatePromises = selectedTasksData.map(task =>
      new Promise((resolve, reject) => {
        outcomeTaskManager.updateOutcomeTask({
          taskId: task.taskId,
          isNotUrgent: true
        })
          .then(() => {
            addNotification(`bulk-normal-${task.taskId}`, `Task for ${task.patient?.name} marked as normal`);
            resolve(task);
          })
          .catch((error) => {
            console.error(`Failed to mark task ${task.taskId} as normal:`, error);
            addNotification(`bulk-normal-error-${task.taskId}`, `Failed to mark task for ${task.patient?.name} as normal`, { isError: true });
            reject(error);
          });
      })
    );

    Promise.allSettled(updatePromises).then(() => {
      setTasks(prevTasks =>
        prevTasks.map(task =>
          selectedTasksData.find(bulkTask => bulkTask.taskId === task.taskId)
            ? { ...task, isUrgent: false }
            : task
        )
      );
      setSelectedRows(new Set());
    });
  };

  const confirmBulkDeleteTasks = () => {
    if (tasksForBulkAction.length === 0) return;

    setBulkDeleteLoading(true);

    const deletePromises = tasksForBulkAction.map(task =>
      new Promise((resolve, reject) => {
        outcomeTaskManager.deleteOutcomeTask(task.taskId)
          .then(() => {
            addNotification(`bulk-delete-${task.taskId}`, `Task for ${task.patient?.name} deleted successfully`);
            resolve(task);
          }).catch((error) => {
            console.error(`Failed to delete task ${task.taskId}:`, error);
            addNotification(`bulk-delete-error-${task.taskId}`, `Failed to delete task for ${task.patient?.name}`, { isError: true });
            reject(error);
          });
      })
    );

    Promise.allSettled(deletePromises).then(() => {
      const deletedTaskIds = tasksForBulkAction.map(task => task.taskId);
      deletedTaskIds.forEach(removeTaskLocally);
      setSelectedRows(new Set());
      setBulkDeleteModalOpen(false);
      setTasksForBulkAction([]);
    }).finally(() => {
      setBulkDeleteLoading(false);
    });
  };

  const confirmBulkMarkComplete = () => {
    if (tasksForBulkAction.length === 0) return;

    setBulkMarkCompleteLoading(true);

    const markCompletePromises = tasksForBulkAction.map(task =>
      new Promise((resolve, reject) => {
        outcomeTaskManager.completeOutcomeTask(task.taskId)
          .then(() => {
            addNotification(`bulk-complete-${task.taskId}`, `Task for ${task.patient?.name} marked complete`);
            resolve(task);
          })
          .catch((error) => {
            console.error(`Failed to mark task ${task.taskId} as complete:`, error);
            addNotification(`bulk-complete-error-${task.taskId}`, `Failed to mark task for ${task.patient?.name} as complete`, { isError: true });
            reject(error);
          });
      })
    );

    Promise.allSettled(markCompletePromises).then(() => {
      tasksForBulkAction.forEach(task => applyTaskPatch(task.taskId, { isComplete: true }));
      setSelectedRows(new Set());
      setBulkMarkCompleteModalOpen(false);
      setTasksForBulkAction([]);
    }).finally(() => {
      setBulkMarkCompleteLoading(false);
    });
  };

  const columns = useMemo(() => [
    // Urgency column (first column) with enum dropdown filter
    createEnumColumn(
      'isUrgent',
      'Urgency',
      (task) => task.isUrgent ? 'URGENT' : 'NORMAL',
      ['URGENT', 'NORMAL'],
      {
        compulsory: true,
        renderer: (value) => {
          const urgency = UrgencyEnum[value];
          return (
            <StatusBadge
              label={urgency.displayName}
              colorFamily={urgency.colorFamily || 'gray'}
              size="normal"
            />
          );
        },
        sortable: true,
        compareFn: (a, b) => {
          // Sort: URGENT (true) comes before NORMAL (false)
          const aUrgent = a.isUrgent || false;
          const bUrgent = b.isUrgent || false;
          if (aUrgent === bUrgent) return 0;
          return aUrgent ? -1 : 1;
        }
      }
    ),

    // Patient Name column
    createTextColumn(
      'patientName',
      'Patient Name',
      (task) => task.patient?.name || 'N/A',
      {
        sortable: true,
        filterable: true
      }
    ),

    // NHS Number column
    createTextColumn(
      'nhsId',
      'NHS Number',
      (task) => {
        const nhsId = task.patient?.nhsId;
        if (!nhsId || nhsId.length !== 10) return 'N/A';
        return `${nhsId.slice(0, 3)} ${nhsId.slice(3, 6)} ${nhsId.slice(6, 10)}`;
      },
      {
        sortable: true,
        filterable: true
      }
    ),

    // Description column (task text)
    createTextColumn(
      'text',
      'Description',
      (task) => task.text,
      {
        sortable: true,
        filterable: true
      }
    ),

    // Status column with IN_REVIEW support
    createEnumColumn(
      'isComplete',
      'Status',
      (task) => {
        // Display logic: Complete, In Review, or Incomplete
        if (task.isComplete) return 'Complete';
        if (hasViewers && hasViewers(task.taskId)) return 'In Review';
        return 'Incomplete';
      },
      ['Complete', 'Incomplete'], // Filter options remain 2 (In Review is display-only)
      {
        compulsory: true,
        renderer: (value, task) => {
          const taskHasViewers = hasViewers && hasViewers(task.taskId);
          let displayStatus;
          let colorFamily;
          
          if (task.isComplete) {
            displayStatus = 'Complete';
            colorFamily = 'green';
          } else if (taskHasViewers) {
            displayStatus = 'In Review';
            colorFamily = 'teal';
          } else {
            displayStatus = 'Incomplete';
            colorFamily = 'yellow';
          }
          return (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <StatusBadge
                label={displayStatus}
                colorFamily={colorFamily}
                size="normal"
              />
              {(() => {
                const commentsCount = task?.comments?.length || 0;
                
                if (commentsCount > 0) {
                  return (
                    <div className="comment-badge-wrapper">
                      <span className="comment-count-badge">{commentsCount}</span>
                      <div className="comment-preview-tooltip">
                        <div className="comment-preview-header">
                          Comments ({commentsCount})
                        </div>
                        <div className="comment-preview-list">
                          {task.comments.slice(0, 3).map((comment, index) => {
                            const commentUser = comment.userId === 'SYSTEM'
                              ? { name: 'MedPrompt App' }
                              : activeUsers.find(u => u.userId === comment.userId);
                            const commentDate = new Date(comment.timestamp);
                            
                            return (
                              <div key={index} className="comment-preview-item">
                                <div className="comment-preview-text">{comment.comment}</div>
                                <div className="comment-preview-meta">
                                  <span className="comment-preview-author">
                                    {commentUser?.name || 'Unknown User'}
                                  </span>
                                  <span className="comment-preview-time">
                                    {commentDate.toLocaleDateString()} {commentDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                          {task.comments.length > 3 && (
                            <div className="comment-preview-more">
                              +{task.comments.length - 3} more comment{task.comments.length - 3 > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          );
        },
        sortable: true,
        compareFn: (a, b) => {
          // 3-way sort: Complete (0) < In Review (1) < Incomplete (2)
          const getOrder = (task) => {
            if (task.isComplete) return 0;
            if (hasViewers && hasViewers(task.taskId)) return 1;
            return 2;
          };
          
          const aOrder = getOrder(a);
          const bOrder = getOrder(b);
          return aOrder - bOrder;
        }
      }
    ),

    // Assignee column
    createTextColumn(
      'assigneeId',
      'Assignee',
      (task) => getAssigneeName(task.assigneeId),
      {
        sortable: true,
        filterable: true,
        renderer: (value, task) => (
          <span className="assignee-with-availability">
            <span>{value}</span>
            <OutOfOfficeMark outOfOffice={outOfOfficeForAssignee(task.assigneeId, activeUsers)} />
          </span>
        ),
      }
    ),

    // Created at Column
    createDateColumn(
      'timestamp',
      'Created at',
      (task) => task.timestamp,
      { compulsory: true }
    ),

    // Actions column
    createCustomColumn(
      'actions',
      'Actions',
      (task) => task,
      (value, task) => (
        <div className="actions-row">
          <ActionButton
            icon={faCheck}
            onClick={() => {
              if (!task.isComplete) {
                handleMarkComplete({
                  taskId: task.taskId
                });
              }
            }}
            disabled={task.isComplete}
            title={task.isComplete ? "Task already completed" : "Mark Complete"}
          />
          <ActionButton
            icon={faTrash}
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this task?')) {
                handleDeleteTask(task.taskId);
              }
            }}
            title="Delete Task"
          />
        </div>
      ),
      {
        compulsory: true,
        sortable: false,
        filterable: false,
        className: 'actions-column'
      }
    ),

    // Optional columns (not in default view)

    // Task type column (optional)
    createEnumColumn(
      'taskType',
      'Task Type',
      (task) => task.taskType,
      Object.values(TaskTypeEnum).map(t => t.value),
      {
        renderer: (value) => TaskTypeEnum[value]?.description || value,
        sortable: true
      }
    )
  ], [getAssigneeName, activeUsers, hasViewers, taskViewers]);

  const {
    columnOrder,
    hiddenColumns,
    saveSettings
  } = useColumnSettings('OutcomeTasksTable', columns, OUTCOME_TASKS_TABLE_DEFAULT_HIDDEN_COLUMNS);

  const hiddenColumnsSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns]);

  const bulkActions = useMemo(() => [
    {
      key: 'markUrgent',
      label: 'Mark as Urgent',
      icon: faExclamationCircle,
      onClick: handleBulkMarkUrgent
    },
    {
      key: 'markNormal',
      label: 'Mark as Normal',
      icon: faCheckCircle,
      onClick: handleBulkMarkNormal
    },
    {
      key: 'markComplete',
      label: 'Mark Complete',
      icon: faCheck,
      onClick: handleBulkMarkComplete
    },
    {
      key: 'deleteTasks',
      label: 'Delete Tasks',
      icon: faTrash,
      onClick: handleBulkDeleteTasks
    }
  ], [handleBulkMarkUrgent, handleBulkMarkNormal, handleBulkMarkComplete, handleBulkDeleteTasks]);

  return {
    columns,
    columnOrder,
    hiddenColumnsSet,
    defaultHidden: OUTCOME_TASKS_TABLE_DEFAULT_HIDDEN_COLUMNS,
    saveSettings,
    columnCustomizationOpen,
    setColumnCustomizationOpen,
    tableSortState,
    setTableSortState,
    tableFilterValues,
    setTableFilterValues,
    tableDisabledFilters,
    setTableDisabledFilters,
    tablePaginationState,
    setTablePaginationState,
    selectedRows,
    setSelectedRows,
    highlightedRows,
    setHighlightedRows,
    bulkActions,
    clearAssigneeFilter,
    bulkDeleteModalOpen,
    setBulkDeleteModalOpen,
    bulkMarkCompleteModalOpen,
    setBulkMarkCompleteModalOpen,
    tasksForBulkAction,
    setTasksForBulkAction,
    bulkDeleteLoading,
    bulkMarkCompleteLoading,
    confirmBulkDeleteTasks,
    confirmBulkMarkComplete,
  };
};

export default useOutcomeTaskTable;
